import {
  Classes,
  FormGroup,
  FormGroupFeedback,
  Frow,
  IFormGroupProps,
  Intent,
  IRadioSelectProps,
  RadioSelect,
} from '@pinpointhq/thumbtack';
import { FieldHelperProps, FieldInputProps, FieldMetaProps, useField, useFormikContext } from 'formik';
import * as React from 'react';

type Value = IRadioSelectProps['value'];

interface IRadioSelectFieldProps<ValueType> {
  booleans?: boolean;
  disabled?: boolean;
  name: string;
  inline?: boolean;
  options: { label: string | React.ReactNode; disabled?: boolean; value: ValueType }[];
  onChange?: (value: ValueType) => void;
  submitOnChange?: boolean;
  formGroupProps?: Omit<IFormGroupProps, 'labelFor'>;
}

export function RadioSelectField<ValueType extends Value>({
  disabled = false,
  formGroupProps,
  inline,
  options,
  submitOnChange,
  onChange,
  name,
}: IRadioSelectFieldProps<ValueType>) {
  const [field, meta, helpers]: [FieldInputProps<Value>, FieldMetaProps<Value>, FieldHelperProps<Value>] =
    useField<Value>(name);
  const hasError = meta.touched && meta.error;

  const { submitForm } = useFormikContext();

  const radios = options.map((option, index) => {
    const onRadioChange = (event: React.FormEvent<HTMLInputElement>) => {
      if (event.currentTarget.checked) helpers.setValue(option.value);
      if (submitOnChange) submitForm();
      if (onChange) onChange(option.value);
    };

    return (
      <RadioSelect
        disabled={option.disabled || disabled}
        aria-label={name}
        intent={hasError ? Intent.DANGER : Intent.NONE}
        style={{ flex: '1 1 auto' }}
        key={option.value.toString()}
        onChange={onRadioChange}
        value={option.value}
        inline={inline}
        checked={field.value === option.value || false}
      >
        <div className={Classes.CONTROL_LABEL}>{option.label}</div>
      </RadioSelect>
    );
  });

  return (
    <FormGroup {...formGroupProps} labelFor={name}>
      <Frow flexDirection={inline ? 'row' : 'column'} flexWrap="nowrap">
        {radios}
      </Frow>
      {hasError && <FormGroupFeedback intent={Intent.DANGER}>{meta.error}</FormGroupFeedback>}
    </FormGroup>
  );
}

export default RadioSelectField;
